import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Divider,
  LinearProgress,
  Button
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { useTranslation } from 'react-i18next';

const OMOP_AGENT_INFO_PANEL_COPY = {
  de: {
    title: 'KI-gestütztes Mapping',
    whySuggestion: '💡 Warum dieser Vorschlag?',
    conceptsInvestigated: '{{count}} OMOP Concepts untersucht',
    model: 'Modell',
    source: 'Quelle',
    timestamp: 'Zeitpunkt',
    unknownModel: 'unbekannt',
    unknownTimestamp: 'unbekannt',
    noRationale: 'Keine Begründung angegeben.',
    stage: 'Stufe',
    alternatives: 'Alternative Kandidaten ({{count}})',
    confidenceHigh: 'Sehr hoch',
    confidenceMedium: 'Mittel',
    confidenceLow: 'Niedrig',
  },
  en: {
    title: 'AI-assisted mapping',
    whySuggestion: '💡 Why this suggestion?',
    conceptsInvestigated: '{{count}} OMOP concepts investigated',
    model: 'Model',
    source: 'Source',
    timestamp: 'Timestamp',
    unknownModel: 'unknown',
    unknownTimestamp: 'unknown',
    noRationale: 'No rationale provided.',
    stage: 'Stage',
    alternatives: 'Alternative candidates ({{count}})',
    confidenceHigh: 'Very high',
    confidenceMedium: 'Medium',
    confidenceLow: 'Low',
  },
  pt: {
    title: 'Mapeamento assistido por IA',
    whySuggestion: '💡 Por que esta sugestão?',
    conceptsInvestigated: '{{count}} conceitos OMOP investigados',
    model: 'Modelo',
    source: 'Fonte',
    timestamp: 'Data/hora',
    unknownModel: 'desconhecido',
    unknownTimestamp: 'desconhecido',
    noRationale: 'Nenhuma justificação indicada.',
    stage: 'Etapa',
    alternatives: 'Candidatos alternativos ({{count}})',
    confidenceHigh: 'Muito alta',
    confidenceMedium: 'Média',
    confidenceLow: 'Baixa',
  },
};

const formatTimestamp = (value, locale) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleString(locale);
};

/**
 * Der reale Kandidaten-Contract (parameters.json) traegt die Begruendung nicht
 * auf `topMatch`/`alternatives` selbst, sondern in
 * `aiMetadata.agent3Output.allCandidates[].reason`, verknuepft ueber
 * `conceptId` (#100 Rueckmeldung Runde 1).
 */
const resolveCandidateRationale = (candidate, agent3Output) => {
  if (candidate?.rationale) {
    return candidate.rationale;
  }
  const allCandidates = Array.isArray(agent3Output?.allCandidates) ? agent3Output.allCandidates : [];
  const match = allCandidates.find((c) => String(c.conceptId) === String(candidate?.conceptId));
  return match?.reason || null;
};

/**
 * `aiMetadata.model` ist das tatsaechlich verwendete Modell (z. B. eines
 * Live-Laufs des Mock-Service). `aiMetadata.source`/`version` benennen
 * dagegen das Multi-Agent-System selbst, nicht das zugrundeliegende Modell --
 * beides unter "Modell" auszugeben waere ein Ersatzwert, der wie die Antwort
 * aussieht (#100 Rueckmeldung Runde 1, Blocker B3). Fehlt `model`, ist das
 * Modell schlicht unbekannt; `source`/`version` werden separat als Quelle
 * ausgegeben.
 */
const resolveSourceLabel = (aiMetadata) => {
  if (!aiMetadata?.source) {
    return null;
  }
  return aiMetadata.version ? `${aiMetadata.source} v${aiMetadata.version}` : aiMetadata.source;
};

/**
 * Zeigt Stufe und Begruendung eines Kandidaten. Fehlt eine Angabe im
 * Kandidaten-Contract, wird das ausgeschrieben statt sie zu verschweigen (#100 AK3).
 */
const CandidateRationale = ({ candidate, agent3Output, copy }) => {
  const rationale = resolveCandidateRationale(candidate, agent3Output);
  return (
    <Box sx={{ mt: 1 }}>
      {candidate.stage && (
        <Chip label={`${copy.stage}: ${candidate.stage}`} size="small" variant="outlined" sx={{ mr: 1, mb: 1 }} />
      )}
      <Typography variant="body2" color={rationale ? 'textSecondary' : 'text.disabled'} fontStyle={rationale ? 'normal' : 'italic'}>
        {rationale || copy.noRationale}
      </Typography>
    </Box>
  );
};

/**
 * OmopAgentInfoPanel Component
 * Erklaert, wie ein Vorschlag entstanden ist -- anhand von Stufe, Begruendung,
 * Modell und Zeitpunkt aus dem Kandidaten-Contract, nicht aus einer erzeugten
 * Erzaehlung (#100).
 *
 * @param {object} props
 * @param {object} props.aiMetadata - { model, timestamp, confidenceScore, totalCandidates }
 * @param {object} props.topMatch - Top matched OMOP concept, inkl. `stage`/`rationale`
 * @param {array} props.alternatives - Alternative candidate concepts, inkl. `stage`/`rationale`
 * @param {boolean} props.defaultExpanded - Whether to show alternatives by default
 */
const OmopAgentInfoPanel = ({
  aiMetadata,
  topMatch,
  alternatives = [],
  defaultExpanded = false
}) => {
  const [showAlternatives, setShowAlternatives] = useState(defaultExpanded);
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage?.startsWith('pt')
    ? 'pt'
    : i18n.resolvedLanguage?.startsWith('en')
      ? 'en'
      : 'de';
  const copy = OMOP_AGENT_INFO_PANEL_COPY[language];
  const locale = language === 'pt' ? 'pt-PT' : language === 'en' ? 'en-US' : 'de-DE';

  if (!aiMetadata) {
    return null;
  }

  const { confidenceScore, totalCandidates, timestamp } = aiMetadata;
  const agent3Output = aiMetadata.agent3Output;
  const model = aiMetadata.model || null;
  const source = resolveSourceLabel(aiMetadata);

  const getConfidenceColor = () => {
    if (confidenceScore >= 0.9) return 'success';
    if (confidenceScore >= 0.7) return 'warning';
    return 'error';
  };

  const getConfidenceLabel = () => {
    if (confidenceScore >= 0.9) return copy.confidenceHigh;
    if (confidenceScore >= 0.7) return copy.confidenceMedium;
    return copy.confidenceLow;
  };

  const formattedTimestamp = formatTimestamp(timestamp, locale);

  return (
    <Paper sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <SmartToyIcon color="primary" />
        <Typography variant="subtitle2" fontWeight="bold">
          {copy.title}
        </Typography>
        <Chip
          label={getConfidenceLabel()}
          color={getConfidenceColor()}
          size="small"
          sx={{ ml: 'auto' }}
        />
      </Box>

      {/* Summary: Why this suggestion? */}
      {topMatch && (
        <Box sx={{ mb: 2, p: 2, backgroundColor: 'white', borderRadius: 1 }}>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            {copy.whySuggestion}
          </Typography>
          <CandidateRationale candidate={topMatch} agent3Output={agent3Output} copy={copy} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
            <Box sx={{ flex: 1 }}>
              <LinearProgress
                variant="determinate"
                value={(confidenceScore || 0) * 100}
                sx={{
                  height: 6,
                  borderRadius: 1,
                  backgroundColor: '#e0e0e0',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor:
                      confidenceScore >= 0.9
                        ? '#4caf50'
                        : confidenceScore >= 0.7
                        ? '#ff9800'
                        : '#f44336',
                    borderRadius: 1
                  }
                }}
              />
            </Box>
            <Typography variant="body2" fontWeight="bold" color="primary">
              {Math.round((confidenceScore || 0) * 100)}%
            </Typography>
          </Box>
          {typeof totalCandidates === 'number' && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
              {copy.conceptsInvestigated.replace('{{count}}', totalCandidates)}
            </Typography>
          )}
        </Box>
      )}

      {/* Model & timestamp -- je Vorschlag sichtbar (#100 AK4) */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="caption" color="textSecondary">
          {copy.model}: {model || copy.unknownModel}
        </Typography>
        {source && (
          <Typography variant="caption" color="textSecondary">
            {copy.source}: {source}
          </Typography>
        )}
        <Typography variant="caption" color="textSecondary">
          {copy.timestamp}: {formattedTimestamp || copy.unknownTimestamp}
        </Typography>
      </Box>

      {/* Alternative Kandidaten */}
      {alternatives.length > 0 && (
        <Box>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setShowAlternatives(!showAlternatives)}
            endIcon={<ExpandMoreIcon sx={{ transform: showAlternatives ? 'rotate(180deg)' : 'none', transition: '0.3s' }} />}
            sx={{ justifyContent: 'space-between', textTransform: 'none' }}
          >
            <Typography variant="body2" fontWeight="medium">
              {copy.alternatives.replace('{{count}}', alternatives.length)}
            </Typography>
          </Button>

          {showAlternatives && (
            <Box sx={{ mt: 1 }}>
              {alternatives.map((candidate, idx) => (
                <React.Fragment key={candidate.conceptId || idx}>
                  {idx > 0 && <Divider sx={{ my: 1 }} />}
                  <Box sx={{ py: 1 }}>
                    <Typography variant="body2" fontWeight="medium">
                      {candidate.conceptName}
                    </Typography>
                    <CandidateRationale candidate={candidate} agent3Output={agent3Output} copy={copy} />
                  </Box>
                </React.Fragment>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default OmopAgentInfoPanel;
